/**
 * clients.ts
 *
 * The typed registry of generated Connect clients, plus the injection seam
 * stores use to obtain it.
 *
 * This module is deliberately free of Pinia and router imports. Stores
 * import it, so any dependency added here would be pulled into every store
 * and would recreate the import cycle that previously booted the router.
 * Real store wiring lives in `appClients.ts`.
 */
import { type App, type InjectionKey, inject } from "vue";
import { createClient } from "@connectrpc/connect";
import type { Transport } from "@connectrpc/connect";

import { AdminFeatureService } from "@/gen/v1/admin_feature_pb";
import { AdminNotificationService } from "@/gen/v1/admin_notification_pb";
import { AdminSurveyService } from "@/gen/v1/admin_survey_pb";
import { AdminCampaignService, CampaignService } from "@/gen/v1/campaign_pb";
import { AdminUserDeviceService } from "@/gen/v1/admin_user_device_pb";
import { BetLimitService } from "@/gen/v1/bet_limit_pb";
import { BetTypeService } from "@/gen/v1/bet_type_pb";
import { BotSettingsService } from "@/gen/v1/bot_settings_pb";
import { CustomerService } from "@/gen/v1/customer_pb";
import { CustomerTelegramService } from "@/gen/v1/customer_telegram_pb";
import { DraftService } from "@/gen/v1/draft_pb";
import { FeatureUsageService } from "@/gen/v1/feature_usage_pb";
import { InquiryService } from "@/gen/v1/inquiry_pb";
import { MaintenanceService } from "@/gen/v1/maintenance_pb";
import { MessageCalculationService } from "@/gen/v1/message_calculation_pb";
import { MessageReportService } from "@/gen/v1/message_report_pb";
import { MessageService } from "@/gen/v1/message_pb";
import { NotificationService } from "@/gen/v1/notification_pb";
import { OnboardingService } from "@/gen/v1/onboarding_pb";
import { ParserAnalyticsService } from "@/gen/v1/parser_analytics_pb";
import { StationService } from "@/gen/v1/station_pb";
import { SurveyService } from "@/gen/v1/survey_pb";
import { AdminFeatureStatsService } from "@/gen/v1/admin_feature_stats_pb";
import { TelegramAnalyticsService } from "@/gen/v1/telegram_analytics_pb";
import { UserManagerService } from "@/gen/v1/user_manager_pb";
import { UserPreferenceService } from "@/gen/v1/user_preference_pb";
import { UserService } from "@/gen/v1/user_pb";
import { UserTelegramService } from "@/gen/v1/user_telegram_pb";
import { WinnerCheckerService } from "@/gen/v1/winner_checker_pb";

/** The two transports a client registry is built over. */
export interface ClientTransports {
  /** Retry only, no error transform. Used by Login/RefreshToken/Logout. */
  publicTransport: Transport;
  /** Full interceptor chain. Used by every authenticated service. */
  authTransport: Transport;
}

/**
 * Builds the full set of Connect clients over the given transports.
 *
 * Only `userClient` uses the public transport; every other client is
 * authenticated. This mirrors the original singleton assignment exactly.
 *
 * @param transports - The public and authenticated transports.
 * @returns A registry of every service client.
 */
export function createClients(transports: ClientTransports) {
  const { publicTransport, authTransport } = transports;

  return {
    // Public clients (Login, RefreshToken, Logout)
    userClient: createClient(UserService, publicTransport),

    // Authenticated clients
    authUserClient: createClient(UserService, authTransport),
    adminFeatureClient: createClient(AdminFeatureService, authTransport),
    adminNotificationClient: createClient(AdminNotificationService, authTransport),
    adminSurveyClient: createClient(AdminSurveyService, authTransport),
    adminCampaignClient: createClient(AdminCampaignService, authTransport),
    adminUserDeviceClient: createClient(AdminUserDeviceService, authTransport),
    betLimitClient: createClient(BetLimitService, authTransport),
    betTypeClient: createClient(BetTypeService, authTransport),
    botSettingsClient: createClient(BotSettingsService, authTransport),
    campaignClient: createClient(CampaignService, authTransport),
    customerClient: createClient(CustomerService, authTransport),
    customerTelegramClient: createClient(CustomerTelegramService, authTransport),
    draftClient: createClient(DraftService, authTransport),
    featureUsageClient: createClient(FeatureUsageService, authTransport),
    inquiryClient: createClient(InquiryService, authTransport),
    maintenanceClient: createClient(MaintenanceService, authTransport),
    messageCalculationClient: createClient(MessageCalculationService, authTransport),
    messageClient: createClient(MessageService, authTransport),
    messageReportClient: createClient(MessageReportService, authTransport),
    notificationClient: createClient(NotificationService, authTransport),
    onboardingClient: createClient(OnboardingService, authTransport),
    parserAnalyticsClient: createClient(ParserAnalyticsService, authTransport),
    adminFeatureStatsClient: createClient(AdminFeatureStatsService, authTransport),
    telegramAnalyticsClient: createClient(TelegramAnalyticsService, authTransport),
    stationClient: createClient(StationService, authTransport),
    surveyClient: createClient(SurveyService, authTransport),
    userManagerClient: createClient(UserManagerService, authTransport),
    userPreferenceClient: createClient(UserPreferenceService, authTransport),
    userTelegramClient: createClient(UserTelegramService, authTransport),
    winnerCheckerClient: createClient(WinnerCheckerService, authTransport),
  };
}

/** The registry of every Connect client available to the application. */
export type AppClients = ReturnType<typeof createClients>;

/** Injection key for the client registry. */
export const clientsKey: InjectionKey<AppClients> = Symbol("appClients");

let defaultClients: AppClients | null = null;

/**
 * Returns the registry registered by `appClients.ts` at startup.
 *
 * Used by callers that run outside a component injection context, such as a
 * store first instantiated from a Connect interceptor.
 *
 * @throws When no registry has been registered yet.
 */
export function getDefaultClients(): AppClients {
  if (!defaultClients) {
    throw new Error(
      "No Connect client registry available. In the app this is set up by " +
        "registerPlugins(); in a test, use createTestApp() from " +
        "tests/helpers/testApp.",
    );
  }
  return defaultClients;
}

/**
 * Registers the registry used when no provider is present.
 *
 * Called once at startup by `appClients.ts`. Tests should not call this;
 * they should provide a registry through `createTestApp` instead.
 *
 * @param clients - The registry to fall back to.
 */
export function setDefaultClients(clients: AppClients): void {
  defaultClients = clients;
}

/**
 * Makes the registry available to every component and store in the app.
 *
 * @param app - The Vue application instance.
 * @param clients - The registry to provide.
 */
export function provideClients(app: App, clients: AppClients): void {
  app.provide(clientsKey, clients);
}

/**
 * Returns the Connect client registry.
 *
 * Resolves the provided registry first, falling back to the one registered
 * by `appClients.ts` at startup.
 *
 * @returns The client registry.
 * @throws When no registry has been provided or registered - which in a
 *   test means the store was instantiated without `createTestApp()`.
 */
export function useClients(): AppClients {
  return inject(clientsKey, null) ?? getDefaultClients();
}
